import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Bookmark, Users, CalendarDays, Hash, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Sidebar = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState({
    profileViews: { total: 0, trend: 0 },
    postImpressions: { total: 0 },
    searchAppearances: { total: 0 }
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Fetch analytics data
  useEffect(() => {
    if (user) {
      fetchAnalytics();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all analytics in parallel
      const [profileViewsRes, postImpressionsRes, searchAppearancesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/profile-views/count`, { headers }).catch(() => ({ data: { total: 0, trend: 0 } })),
        axios.get(`${API_BASE_URL}/api/analytics/post-impressions`, { headers }).catch(() => ({ data: { total_impressions: 0 } })),
        axios.get(`${API_BASE_URL}/api/analytics/search-appearances`, { headers }).catch(() => ({ data: { total: 0 } }))
      ]);

      setAnalytics({
        profileViews: {
          total: profileViewsRes.data.total || 0,
          trend: profileViewsRes.data.trend || 0
        },
        postImpressions: {
          total: postImpressionsRes.data.total_impressions || 0
        },
        searchAppearances: {
          total: searchAppearancesRes.data.total || 0
        }
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const quickLinks = [
    { 
      icon: Eye, 
      label: 'Who viewed your profile', 
      count: loadingAnalytics ? '...' : analytics.profileViews.total.toString(), 
      trend: analytics.profileViews.trend !== 0 ? (analytics.profileViews.trend > 0 ? `+${analytics.profileViews.trend}` : analytics.profileViews.trend.toString()) : null
    },
    { icon: Bookmark, label: 'Saved items', count: null },
    { icon: Users, label: 'Groups', count: '8' },
    { icon: CalendarDays, label: 'Events', count: '3', trend: '+2' },
    { icon: Hash, label: 'Hashtags', count: null }
  ];

  const recentGroups = [
    'Corporate Training Professionals',
    'E-Learning Innovators', 
    'Leadership Development Network',
    'Training ROI Specialists',
    'VR Training Pioneers'
  ];

  const handleViewProfileViewers = () => {
    console.log('Profile viewers clicked - showing analytics');
  };

  const handleSavedItems = () => {
    console.log('Saved items clicked');
  };

  const handleGroups = () => {
    window.location.href = '/groups';
  };

  const handleEvents = () => {
    window.location.href = '/events';
  };

  const handleHashtags = () => {
    console.log('Hashtags clicked');
  };

  const handleAccessTools = () => {
    console.log('Access exclusive tools clicked');
  };

  const handleShowAllGroups = () => {
    window.location.href = '/groups';
  };

  const handleGroupClick = (groupName) => {
    console.log(`Group clicked: ${groupName}`);
  };

  return (
    <div className="space-y-4 desktop-sidebar">
      {/* Profile Card */}
      <Card className="card-hover overflow-hidden">
        <div className="relative">
          <div className="h-16 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-20"></div>
          </div>
          <div className="absolute -bottom-8 left-4">
            <Avatar className="w-16 h-16 border-4 border-white shadow-soft avatar-hover">
              <AvatarImage src={user?.avatar} alt={user?.name} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold text-lg">
                {user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
        <CardContent className="pt-10 pb-4">
          <Link to="/profile" className="block group">
            <h3 className="font-semibold text-sm group-hover:text-blue-600 transition-colors duration-200">
              {user?.name}
            </h3>
            <p className="text-xs text-gray-600 mb-3 line-clamp-1">{user?.title}</p>
          </Link>
          
          <Separator className="my-3" />
          
          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-center hover:bg-blue-50 p-2 rounded cursor-pointer sidebar-item group transition-all duration-200"
                 onClick={handleViewProfileViewers}>
              <span className="text-gray-600 group-hover:text-blue-700">Profile viewers</span>
              <div className="flex items-center space-x-1">
                {loadingAnalytics ? (
                  <span className="text-gray-400 text-xs">...</span>
                ) : (
                  <>
                    <span className="text-blue-600 font-semibold">{analytics.profileViews.total}</span>
                    {analytics.profileViews.trend !== 0 && (
                      <Badge variant="secondary" className={`text-xs px-1 h-4 ${analytics.profileViews.trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        <TrendingUp className={`w-2 h-2 mr-0.5 ${analytics.profileViews.trend < 0 ? 'rotate-180' : ''}`} />
                        {analytics.profileViews.trend > 0 ? '+' : ''}{analytics.profileViews.trend}
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center hover:bg-blue-50 p-2 rounded cursor-pointer sidebar-item group transition-all duration-200">
              <span className="text-gray-600 group-hover:text-blue-700">Post impressions</span>
              {loadingAnalytics ? (
                <span className="text-gray-400 text-xs">...</span>
              ) : (
                <span className="text-blue-600 font-semibold">{analytics.postImpressions.total.toLocaleString()}</span>
              )}
            </div>
            <div className="flex justify-between items-center hover:bg-blue-50 p-2 rounded cursor-pointer sidebar-item group transition-all duration-200">
              <span className="text-gray-600 group-hover:text-blue-700">Search appearances</span>
              {loadingAnalytics ? (
                <span className="text-gray-400 text-xs">...</span>
              ) : (
                <span className="text-blue-600 font-semibold">{analytics.searchAppearances.total}</span>
              )}
            </div>
          </div>
          
          <Separator className="my-3" />
          
          <Button 
            variant="ghost" 
            className="w-full text-xs justify-start p-2 h-auto font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
            onClick={handleAccessTools}
          >
            <Eye className="w-3 h-3 mr-2" />
            Access exclusive tools & insights
          </Button>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card className="card-hover">
        <CardContent className="p-3">
          <div className="space-y-1">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              const getClickHandler = () => {
                switch(link.label) {
                  case 'Who viewed your profile': return handleViewProfileViewers;
                  case 'Saved items': return handleSavedItems;
                  case 'Groups': return handleGroups;
                  case 'Events': return handleEvents;
                  case 'Hashtags': return handleHashtags;
                  default: return () => console.log(`${link.label} clicked`);
                }
              };
              
              return (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-2 rounded sidebar-item cursor-pointer group transition-all duration-200"
                  onClick={getClickHandler()}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors duration-200" />
                    <span className="text-sm text-gray-800 group-hover:text-blue-700 transition-colors duration-200">
                      {link.label}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {link.count && (
                      <span className="text-sm text-gray-600 font-medium">{link.count}</span>
                    )}
                    {link.trend && (
                      <Badge variant="secondary" className="text-xs px-1 h-4 bg-green-100 text-green-700">
                        {link.trend}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <Separator className="my-3" />
          
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-gray-800 px-2">Recent groups</h4>
            {recentGroups.slice(0, 4).map((group, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-2 p-2 rounded sidebar-item cursor-pointer group transition-all duration-200"
                onClick={() => handleGroupClick(group)}
              >
                <Hash className="w-3 h-3 text-gray-600 group-hover:text-blue-600 transition-colors duration-200" />
                <span className="text-xs text-gray-800 truncate group-hover:text-blue-700 transition-colors duration-200">
                  {group}
                </span>
              </div>
            ))}
            <Button 
              variant="ghost" 
              className="w-full text-xs justify-start p-2 h-auto font-medium text-blue-600 hover:bg-blue-50 transition-all duration-200"
              onClick={handleShowAllGroups}
            >
              Show all groups ({recentGroups.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Premium Upgrade Card */}
      <Card className="card-hover border-2 border-gradient-to-r from-yellow-200 to-orange-200 bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/10 to-orange-400/10 rounded-full -mr-10 -mt-10"></div>
        <CardContent className="p-4 relative">
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-soft">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-semibold text-sm mb-2 text-gray-800">Get hired faster</h4>
            <p className="text-xs text-gray-600 mb-3 line-height-relaxed">
              Access premium training job opportunities and stand out to recruiters
            </p>
            <Button size="sm" className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-xs shadow-soft btn-hover border-none">
              Try Premium Free
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer Links */}
      <div className="px-2 py-3">
        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">About</a>
          <span>•</span>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Accessibility</a>
          <span>•</span>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Help Center</a>
          <span>•</span>
          <a href="#" className="hover:text-blue-600 transition-colors duration-200">Privacy</a>
        </div>
        <p className="text-xs text-gray-400 mt-2">TrainerLinked © 2024</p>
      </div>
    </div>
  );
};

export default Sidebar;